'use client';

import { usePathname } from 'next/navigation';
import { LOCALE_META, toLocale, type Locale } from '@mentorivo/shared/locales';

/**
 * SON çare hata sınırı — kök layout'un kendisi patladığında.
 *
 * ---------------------------------------------------------------------------
 * Neden bu dosya farklı kurallarla yazılıyor
 * ---------------------------------------------------------------------------
 * Buraya düşüldüğünde kök layout render EDİLEMEMİŞ demektir. Yani:
 *
 *   • `<html>` ve `<body>` etiketlerini bu bileşen kendisi basmak zorunda.
 *   • next-intl sağlayıcısı yok → `useTranslations` çağrılamaz, metin gömülü.
 *   • `globals.css` yüklenmemiş olabilir → stil satır içi.
 *
 * Bu yüzden ekran bilerek çıplak. Amacı güzel görünmek değil, kullanıcıyı
 * tarayıcının beyaz "Application error" sayfasında bırakmamak: ne olduğunu
 * söyleyen bir cümle ve çalışan bir çıkış.
 *
 * `[locale]/error.tsx` normal hataları zaten yakalıyor; buraya ancak çok
 * daha derin bir arıza düşüyor.
 *
 * ---------------------------------------------------------------------------
 * Metin neden bu dosyada, 13 dilde (29 Eyl 2026)
 * ---------------------------------------------------------------------------
 * Mesaj dosyaları next-intl sağlayıcısıyla geliyor ve sağlayıcı kök layout'ta
 * — yani burada YOK. Üç kısa cümle dosyanın içinde duruyor; dil, adres
 * çubuğundaki önekten okunuyor (`/de/…` → Almanca). Önek yoksa ya da
 * tanınmıyorsa İngilizce. Metinler DeepL betiğinin kapsamında değil, elle
 * yazıldı: bu ekran çeviri altyapısının kendisi çöktüğünde de açılmalı.
 */
const COPY: Record<Locale, { title: string; body: string; retry: string }> = {
  en: {
    title: 'Something went wrong',
    body: 'The application ran into an unexpected error. Trying again is usually enough.',
    retry: 'Try again',
  },
  tr: {
    title: 'Bir şeyler ters gitti',
    body: 'Uygulama beklenmedik bir hatayla karşılaştı. Tekrar denemek çoğu zaman yeterli oluyor.',
    retry: 'Tekrar dene',
  },
  de: {
    title: 'Etwas ist schiefgelaufen',
    body: 'Die Anwendung ist auf einen unerwarteten Fehler gestoßen. Ein erneuter Versuch reicht meistens aus.',
    retry: 'Erneut versuchen',
  },
  fr: {
    title: 'Un problème est survenu',
    body: 'L’application a rencontré une erreur inattendue. Réessayer suffit généralement.',
    retry: 'Réessayer',
  },
  es: {
    title: 'Algo salió mal',
    body: 'La aplicación encontró un error inesperado. Volver a intentarlo suele bastar.',
    retry: 'Reintentar',
  },
  it: {
    title: 'Qualcosa è andato storto',
    body: 'L’applicazione ha riscontrato un errore imprevisto. Di solito basta riprovare.',
    retry: 'Riprova',
  },
  pt: {
    title: 'Algo deu errado',
    body: 'O aplicativo encontrou um erro inesperado. Tentar novamente costuma resolver.',
    retry: 'Tentar novamente',
  },
  ru: {
    title: 'Что-то пошло не так',
    body: 'В приложении произошла непредвиденная ошибка. Обычно достаточно повторить попытку.',
    retry: 'Повторить',
  },
  nl: {
    title: 'Er is iets misgegaan',
    body: 'De applicatie is op een onverwachte fout gestuit. Opnieuw proberen is meestal voldoende.',
    retry: 'Opnieuw proberen',
  },
  ar: {
    title: 'حدث خطأ ما',
    body: 'واجه التطبيق خطأً غير متوقع. عادةً ما تكفي إعادة المحاولة.',
    retry: 'إعادة المحاولة',
  },
  ko: {
    title: '문제가 발생했습니다',
    body: '애플리케이션에서 예기치 않은 오류가 발생했습니다. 대부분 다시 시도하면 해결됩니다.',
    retry: '다시 시도',
  },
  zh: {
    title: '出了点问题',
    body: '应用遇到了意外错误。通常重试一次即可解决。',
    retry: '重试',
  },
  ja: {
    title: '問題が発生しました',
    body: 'アプリケーションで予期しないエラーが発生しました。多くの場合、再試行すれば解決します。',
    retry: '再試行',
  },
};

export default function GlobalError({
  error,
  retry,
}: {
  error: Error & { digest?: string };
  retry: () => void;
}) {
  const locale = toLocale(usePathname()?.split('/')[1]);
  const copy = COPY[locale];

  return (
    <html lang={locale} dir={LOCALE_META[locale].dir}>
      <body
        style={{
          alignItems: 'center',
          backgroundColor: '#0b0d14',
          color: '#e7e9ee',
          display: 'flex',
          fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif',
          justifyContent: 'center',
          margin: 0,
          minHeight: '100dvh',
          padding: '24px',
        }}
      >
        <div style={{ maxWidth: '26rem', textAlign: 'center' }}>
          <h1 style={{ fontSize: 'clamp(1.125rem, 1rem + 0.5vw, 1.375rem)', margin: '0 0 8px' }}>
            {copy.title}
          </h1>
          <p style={{ color: '#9aa1b1', fontSize: '0.875rem', lineHeight: 1.5, margin: 0 }}>
            {copy.body}
          </p>

          <button
            type="button"
            onClick={retry}
            style={{
              backgroundColor: '#e7e9ee',
              border: 0,
              borderRadius: '10px',
              color: '#0b0d14',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 600,
              marginTop: '20px',
              minHeight: '44px',
              padding: '0 20px',
            }}
          >
            {copy.retry}
          </button>

          {/* Destek istendiğinde sunucu logundaki satırla eşleşen tek iz. */}
          {error.digest ? (
            <p style={{ color: '#5c6375', fontSize: '0.6875rem', marginTop: '16px' }}>
              {error.digest}
            </p>
          ) : null}
        </div>
      </body>
    </html>
  );
}
